import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

function formatViews(views: number): string {
  if (views >= 10000) {
    return (views / 10000).toFixed(1) + '万';
  }
  return views.toString();
}

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const articles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950">分类不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <section className="relative w-full h-[280px] md:h-[380px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dynamic%20abstract%20composition%20with%20flowing%20warm%20coral%20and%20gold%20color%20bands%2C%20motion%20blur%20effect%2C%20editorial%20magazine%20style%2C%20dramatic%20atmosphere%2C%20soft%20glow%2C%20professional%20photography%2C%20clean%20minimal%20base%20with%20cream%20tones&width=1800&height=600&seq=category-hero-001&orientation=landscape"
            alt={category.name}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/25 to-black/50"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-3xl md:text-5xl font-black font-sans text-background-50">
              {category.name}
            </h1>
            <p className="mt-3 text-lg md:text-2xl font-heading italic text-background-50/85">
              共 {category.count} 篇内容
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-10 md:py-16">
          <div className="w-full px-4 md:px-8 max-w-[1400px] mx-auto">
            <div className="mb-8">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article, index) => {
                const isMiddle = index % 3 === 1;
                return (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className={`group block rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer ${
                      isMiddle
                        ? 'bg-secondary-900'
                        : 'bg-background-100 hover:bg-background-200/70'
                    }`}
                  >
                    {isMiddle ? (
                      <div className="h-[280px] flex flex-col items-center justify-center p-8 text-center">
                        <span className="w-14 h-14 flex items-center justify-center">
                          <i className="ri-fire-line text-3xl text-accent-400"></i>
                        </span>
                        <h2 className="mt-4 text-lg font-bold text-background-50 leading-snug line-clamp-3">
                          {article.title}
                        </h2>
                        <p className="mt-3 text-sm text-secondary-300 leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                        <span className="mt-4 text-xs text-secondary-400">{article.date} · {article.readTime}</span>
                      </div>
                    ) : (
                      <>
                        <div className="relative w-full h-[220px] overflow-hidden">
                          <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                          {article.isVideo && (
                            <span className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 backdrop-blur-sm text-background-50">
                              <i className="ri-play-fill text-lg"></i>
                            </span>
                          )}
                        </div>
                        <div className="p-5">
                          <h2 className="text-base font-bold text-foreground-950 leading-snug group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                            {article.title}
                          </h2>
                          <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
                            <span>{article.date}</span>
                            <span>{formatViews(article.views)} 阅读</span>
                          </div>
                        </div>
                      </>
                    )}
                  </Link>
                );
              })}
            </div>

            {articles.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
              </div>
            )}
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-8 max-w-[1400px] mx-auto">
            <div className="text-center mb-12">
              <span className="inline-flex items-center gap-2 text-sm text-foreground-400">
                <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                吃瓜群众热评
              </span>
              <h2 className="mt-3 text-2xl md:text-3xl lg:text-4xl font-black text-foreground-950">
                大家都在聊什么
              </h2>
              <p className="mt-2 text-sm text-foreground-500">百万用户真实讨论，一起吃瓜更精彩</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((_, i) => (
                <div key={i} className={`bg-background-50 rounded-2xl p-6 ${i === 2 ? 'opacity-60' : ''}`}>
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span key={star} className="w-4 h-4 flex items-center justify-center text-primary-500">
                        <i className="ri-star-fill text-sm"></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm font-semibold text-foreground-950 mb-2">
                    {i === 0 ? '更新速度太快了，比微博还快！' : i === 1 ? '内容质量好，深度分析有料' : '刷到停不下来...'}
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {i === 0
                      ? '每天必刷的网站，有什么大瓜第一时间就能看到，推送机制也很及时。'
                      : i === 1
                      ? '不像其他网站只会搬运，这里的深度扒皮文章写得很有水平。'
                      : '自从用了91免费，感觉自己与娱乐圈零距离...'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}